import { fetchApi, fetchGetFileApi } from '../client';
import { buildQS } from '../helpers';
import type { Appointment, CreateAppointmentRequest, AppointmentStatus } from '../../../types';

export const appointmentsModule = {
  create: (data: CreateAppointmentRequest) =>
    fetchApi<Appointment>('/api/appointments', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  checkForDuplicate: (patientId: string, doctorId: string, visitDate: string) =>
    fetchApi<{
      found: boolean;
      message: string;
      tokenId?: string;
      tokenNumber?: number;
      status?: string;
      createdAt?: string;
    }>(`/api/appointments/check-duplicate${buildQS({ patientId, doctorId, visitDate })}`),

  getTodayByDoctor: (doctorId: string) =>
    fetchApi<Appointment[]>(`/api/appointments/today?doctorId=${doctorId}`),

  getNextByDoctor: (doctorId: string) =>
    fetchApi<Appointment>(`/api/appointments/next?doctorId=${doctorId}`),

  updateStatus: (id: string, status: AppointmentStatus) =>
    fetchApi<Appointment>(`/api/appointments/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    }),

  /**
   * Marks the patient as physically present — separate from status. A token can exist before the
   * patient arrives (a follow-up booked ahead, a patient-portal self-booking); the front-desk
   * walk-in flow calls this immediately after booking since staff is looking at the patient right
   * then. Idempotent on the backend.
   */
  markArrived: (id: string) =>
    fetchApi<Appointment>(`/api/appointments/${id}/arrive`, { method: 'PATCH' }),

  getRange: (startDate: string, endDate: string, doctorId?: string) =>
    fetchApi<Appointment[]>(`/api/appointments/range${buildQS({ startDate, endDate, doctorId })}`),

  getAll: (date?: string, doctorId?: string) =>
    fetchApi<Appointment[]>(`/api/appointments${buildQS({ date, doctorId })}`),

  getById: (id: string) =>
    fetchApi<Appointment>(`/api/appointments/${id}`),

  delete: (id: string) =>
    fetchApi(`/api/appointments/${id}`, { method: 'DELETE' }),

  print: (id: string) =>
    fetchGetFileApi(`/api/appointments/${id}/print`),

  // Recurring appointments sub-module
  recurringAppointments: {
    create: (data: any) =>
      fetchApi<any>('/api/recurring-appointments', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    pauseRule: (ruleId: string) =>
      fetchApi<any>(`/api/recurring-appointments/${ruleId}/pause`, { method: 'POST' }),
    resumeRule: (ruleId: string) =>
      fetchApi<any>(`/api/recurring-appointments/${ruleId}/resume`, { method: 'POST' }),
    cancelRule: (ruleId: string) =>
      fetchApi<any>(`/api/recurring-appointments/${ruleId}/cancel`, { method: 'POST' }),
  },
};
